/**
 * BrokerSelectCustomerPage — Customer selection for new application
 * Epic 3C: Broker-Initiated Customer Onboarding
 *
 * Replaces the old dropdown modal in BrokerApplicationKanban.
 * Shows connected customers, allows adding new customers by email,
 * and creates applications from selected connections.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import AppHeader from '../components/AppHeader'
import { ArrowLeft, Plus, Search, UserPlus, X, AlertCircle, CheckCircle } from 'lucide-react'
import {
  getBrokerCustomers,
  brokerCreateConnection,
  createApplication,
  type BrokerCustomer,
} from '../api/brokerApi'

export default function BrokerSelectCustomerPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { getAccessToken } = useAuth()

  // Customer list state
  const [customers, setCustomers] = useState<BrokerCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  // Add new customer form state
  const [showAddForm, setShowAddForm] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addName, setAddName] = useState('')
  const [addPhone, setAddPhone] = useState('')
  const [addLeadId, setAddLeadId] = useState('')
  const [addError, setAddError] = useState('')
  const [addSuccess, setAddSuccess] = useState('')
  const [adding, setAdding] = useState(false)

  // Create application state
  const [selectedConnection, setSelectedConnection] = useState<BrokerCustomer | null>(null)
  const [loanPurpose, setLoanPurpose] = useState('')
  const [creating, setCreating] = useState(false)

  // Pre-fill from lead query params
  useEffect(() => {
    const leadId = searchParams.get('lead_id')
    const email = searchParams.get('email')
    const name = searchParams.get('name')
    const phone = searchParams.get('phone')
    if (leadId || email) {
      setShowAddForm(true)
      if (email) setAddEmail(email)
      if (name) setAddName(name)
      if (phone) setAddPhone(phone)
      if (leadId) setAddLeadId(leadId)
    }
  }, [searchParams])

  const loadCustomers = useCallback(async () => {
    const token = getAccessToken()
    if (!token) return
    try {
      setLoading(true)
      const res = await getBrokerCustomers(token, search || undefined)
      setCustomers(res.customers || [])
    } catch {
      /* ignore */
    } finally {
      setLoading(false)
    }
  }, [getAccessToken, search])

  useEffect(() => {
    loadCustomers()
  }, [loadCustomers])

  const handleAddCustomer = async () => {
    const token = getAccessToken()
    if (!token || !addEmail.trim()) return
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(addEmail.trim())) {
      setAddError('Please enter a valid email address')
      return
    }
    setAdding(true)
    setAddError('')
    setAddSuccess('')
    try {
      const result = await brokerCreateConnection(token, {
        email: addEmail.trim(),
        name: addName.trim() || undefined,
        phone: addPhone.trim() || undefined,
        lead_id: addLeadId || undefined,
      })
      if (result.status === 'active') {
        setAddSuccess(`Connected to ${result.customer_name || addEmail}. You can now create an application.`)
      } else {
        setAddSuccess(`Invitation created for ${addEmail}. Customer needs to register to activate.`)
      }
      setAddEmail('')
      setAddName('')
      setAddPhone('')
      setAddLeadId('')
      loadCustomers()
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add customer')
    } finally {
      setAdding(false)
    }
  }

  const handleCreateApplication = async () => {
    const token = getAccessToken()
    if (!token || !selectedConnection) return
    setCreating(true)
    try {
      await createApplication(token, {
        connection_id: selectedConnection.connection_id,
        loan_purpose: loanPurpose || undefined,
      })
      const from = searchParams.get('from')
      navigate(from ? `/broker?tab=${from}` : '/broker')
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to create application')
      setCreating(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => {
              const from = searchParams.get('from')
              navigate(from ? `/broker?tab=${from}` : '/broker')
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900">Select Customer for New Application</h1>
        </div>

        {/* Search + Add button */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => { setShowAddForm(!showAddForm); setAddError(''); setAddSuccess('') }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4" /> Add New Customer
          </button>
        </div>

        {/* Add New Customer Form */}
        {showAddForm && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add New Customer
              </h3>
              <button onClick={() => { setShowAddForm(false); setAddError(''); setAddSuccess('') }} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Email (required)</label>
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Name (optional)</label>
                <input
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Phone (optional)</label>
                <input
                  value={addPhone}
                  onChange={(e) => setAddPhone(e.target.value)}
                  placeholder="0412 345 678"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleAddCustomer}
                disabled={!addEmail.trim() || adding}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {adding ? 'Adding...' : 'Add Customer'}
              </button>
              {addError && (
                <div className="flex items-center gap-1.5 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {addError}
                </div>
              )}
              {addSuccess && (
                <div className="flex items-center gap-1.5 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {addSuccess}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Application Mini Form (shown when customer selected) */}
        {selectedConnection && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">
                New Application for {selectedConnection.name || selectedConnection.email}
              </h3>
              <button onClick={() => setSelectedConnection(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs text-gray-600 block mb-1">Loan Purpose (optional)</label>
                <input
                  value={loanPurpose}
                  onChange={(e) => setLoanPurpose(e.target.value)}
                  placeholder="e.g., Purchase, Refinance, Investment"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <button
                onClick={handleCreateApplication}
                disabled={creating}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Application'}
              </button>
            </div>
          </div>
        )}

        {/* Customer List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading customers...</div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No connected customers found. Use "Add New Customer" to get started.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Connected</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {customers.map((c) => (
                  <tr key={c.connection_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {c.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.phone || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        c.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {c.status === 'pending_invite' ? 'Pending Invite' : c.status}
                      </span>
                      {c.initiated_by === 'broker' && (
                        <span className="ml-1 text-xs text-gray-400">(broker-added)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {c.connected_at ? new Date(c.connected_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {c.status === 'active' && !c.has_active_application ? (
                        <button
                          onClick={() => { setSelectedConnection(c); setLoanPurpose('') }}
                          className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Select
                        </button>
                      ) : c.has_active_application ? (
                        <span className="text-xs text-gray-400">Has active app</span>
                      ) : c.status === 'pending_invite' ? (
                        <span className="text-xs text-amber-600 font-medium">Awaiting signup</span>
                      ) : (
                        <span className="text-xs text-gray-400">Not active</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
